import React from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Image,
  Dimensions,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { ArrowLeft, Users, Target, Heart, Shield } from 'lucide-react-native';

const { width } = Dimensions.get('window');

export default function SobrePage() {
  const router = useRouter();
  const { colors } = useTheme();

  // Breakpoints responsivos
  const isWeb = width > 1024;
  const isTablet = width > 768 && width <= 1024;
  const isMobile = width <= 768;

  const handleGoBack = () => {
    router.back();
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scrollContainer: {
      flexGrow: 1,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 20 : isTablet ? 16 : 12,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      gap: 16,
    },
    backButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: colors.primary + '20',
    },
    headerTitle: {
      fontSize: 20,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    content: {
      paddingHorizontal: isWeb ? 32 : isTablet ? 24 : 16,
      paddingVertical: isWeb ? 40 : isTablet ? 32 : 24,
      maxWidth: isWeb ? 800 : '100%',
      alignSelf: 'center',
      width: '100%',
    },
    heroSection: {
      alignItems: 'center',
      marginBottom: isWeb ? 60 : isTablet ? 48 : 32,
    },
    logo: {
      height: 60,
      width: 180,
      marginBottom: 24,
    },
    mainTitle: {
      fontSize: isWeb ? 36 : isTablet ? 30 : 24,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      textAlign: 'center',
      marginBottom: 16,
      lineHeight: isWeb ? 44 : isTablet ? 38 : 32,
    },
    subtitle: {
      fontSize: isWeb ? 20 : isTablet ? 18 : 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: isWeb ? 28 : isTablet ? 26 : 24,
    },
    section: {
      marginBottom: isWeb ? 48 : isTablet ? 40 : 32,
    },
    sectionTitle: {
      fontSize: isWeb ? 28 : isTablet ? 24 : 20,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
      textAlign: 'center',
    },
    sectionText: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      lineHeight: 24,
      textAlign: 'justify',
      marginBottom: 16,
    },
    valuesGrid: {
      gap: 24,
    },
    valueCard: {
      backgroundColor: colors.surface,
      padding: isWeb ? 32 : isTablet ? 24 : 20,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
      elevation: 2,
      ...(Platform.OS === 'web' ? {
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      } : {
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
      }),
    },
    valueIcon: {
      width: 60,
      height: 60,
      borderRadius: 30,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 16,
    },
    valueTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    valueDescription: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      lineHeight: 20,
      textAlign: 'center',
    },
  });

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleGoBack}
            activeOpacity={0.7}
          >
            <ArrowLeft size={20} color={colors.primary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Sobre Nós</Text>
        </View>

        <View style={styles.content}>
          {/* Hero Section */}
          <View style={styles.heroSection}>
            <Image
              source={require('@/public/icon.png')}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text style={styles.mainTitle}>
              Nossa Missão
            </Text>
            <Text style={styles.subtitle}>
              Simplificar a vida financeira de grupos e fortalecer relacionamentos através da transparência
            </Text>
          </View>

          {/* Missão */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Por que Existimos</Text>
            <Text style={styles.sectionText}>
              Nascemos para resolver um problema universal: a complexidade e o desconforto de dividir despesas entre amigos, familiares e colegas. Quantas vezes você já se viu em situações constrangedoras tentando calcular quem deve quanto após uma viagem, festa ou jantar em grupo?
            </Text>
            <Text style={styles.sectionText}>
              O Põe no Racha surgiu da necessidade de transformar esses momentos de tensão em experiências simples e transparentes. Acreditamos que as finanças não devem ser motivo de conflito entre pessoas que se importam umas com as outras.
            </Text>
            <Text style={styles.sectionText}>
              Nossa plataforma foi desenvolvida com o objetivo de democratizar o acesso a ferramentas financeiras inteligentes, permitindo que qualquer grupo, independente do tamanho ou complexidade das despesas, possa organizar suas finanças de forma justa, transparente e sem complicações.
            </Text>
          </View>

          {/* Nossa História */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Nossa História</Text>
            <Text style={styles.sectionText}>
              Tudo começou com uma viagem entre amigos que quase terminou em discussão por causa das contas. Planilhas confusas, cálculos manuais com erros e a dificuldade de acompanhar quem pagou o quê criaram um ambiente de desconfiança desnecessário.
            </Text>
            <Text style={styles.sectionText}>
              Foi então que decidimos criar uma solução que pudesse eliminar essas frustrações. Após meses de desenvolvimento e testes com grupos reais, nasceu o Põe no Racha - um aplicativo que não apenas calcula divisões de forma precisa, mas também mantém um histórico completo e transparente de todas as transações.
            </Text>
            <Text style={styles.sectionText}>
              Hoje, milhares de usuários já utilizaram nossa plataforma para organizar suas finanças em grupo, desde pequenos jantares até viagens internacionais complexas. Cada feedback nos motiva a continuar inovando e melhorando a experiência de nossos usuários.
            </Text>
          </View>

          {/* Nossos Valores */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Nossos Valores</Text>

            <View style={styles.valuesGrid}>
              <View style={styles.valueCard}>
                <View style={[styles.valueIcon, { backgroundColor: colors.primary + '20' }]}>
                  <Target size={30} color={colors.primary} />
                </View>
                <Text style={styles.valueTitle}>Transparência</Text>
                <Text style={styles.valueDescription}>
                  Acreditamos que a transparência total é fundamental para manter a confiança entre as pessoas. Todos os cálculos, divisões e históricos são completamente visíveis para todos os participantes do grupo.
                </Text>
              </View>

              <View style={styles.valueCard}>
                <View style={[styles.valueIcon, { backgroundColor: colors.success + '20' }]}>
                  <Users size={30} color={colors.success} />
                </View>
                <Text style={styles.valueTitle}>Simplicidade</Text>
                <Text style={styles.valueDescription}>
                  Desenvolvemos nossa interface pensando em pessoas de todas as idades e níveis de conhecimento tecnológico. Usar o Põe no Racha deve ser tão simples quanto conversar com um amigo.
                </Text>
              </View>

              <View style={styles.valueCard}>
                <View style={[styles.valueIcon, { backgroundColor: colors.warning + '20' }]}>
                  <Heart size={30} color={colors.warning} />
                </View>
                <Text style={styles.valueTitle}>Relacionamentos</Text>
                <Text style={styles.valueDescription}>
                  Nosso maior objetivo é fortalecer relacionamentos, não complicá-los. Cada funcionalidade é pensada para reduzir atritos e aumentar a harmonia entre as pessoas que dividem despesas.
                </Text>
              </View>

              <View style={styles.valueCard}>
                <View style={[styles.valueIcon, { backgroundColor: colors.error + '20' }]}>
                  <Shield size={30} color={colors.error} />
                </View>
                <Text style={styles.valueTitle}>Segurança</Text>
                <Text style={styles.valueDescription}>
                  A segurança dos seus dados é nossa prioridade máxima. Utilizamos as melhores práticas de segurança da indústria e plataformas confiáveis como Firebase e Stripe para proteger suas informações.
                </Text>
              </View>
            </View>
          </View>

          {/* Compromisso */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Nosso Compromisso</Text>
            <Text style={styles.sectionText}>
              Estamos comprometidos em continuar evoluindo e inovando para atender às necessidades reais de nossos usuários. Cada atualização, cada nova funcionalidade e cada melhoria é resultado do feedback direto de quem usa o aplicativo no dia a dia.
            </Text>
            <Text style={styles.sectionText}>
              Nosso objetivo é nos tornar a plataforma de referência mundial para divisão de despesas em grupo, mantendo sempre nossos valores de simplicidade, transparência e foco nos relacionamentos humanos.
            </Text>
            <Text style={styles.sectionText}>
              Junte-se a nós nessa jornada de transformar a forma como as pessoas lidam com finanças compartilhadas. Porque acreditamos que dividir despesas deve unir pessoas, não separá-las.
            </Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
